import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useEffect,
} from 'react';

import IProduct from '~/interfaces/IProduct';
import IComplement from '~/interfaces/IComplement';
import ICategory from '~/interfaces/ICategory';
import { bff } from '~/services/bff';
import heroku from '~/services/heroku';
import { useAuth } from './auth';

interface ProductsCompanyContextData {
  categories: ICategory[];
  products: IProduct[];
  complements: IComplement[] | null;
  loadProducts(): Promise<void>;
  loadComplements(): Promise<void>;
  clearContext(): void;
  updateProducts(product: IProduct, isNew?: boolean): void;
  removeProduct(id: string): void;
  updateComplements(complement: IComplement, isNew?: boolean): void;
  removeComplement(id: string): void;
  isInitialized: boolean;
  categorySelected: ICategory;
  setCategorySelected(value: ICategory): void;
  productSelected: IProduct;
  selectProduct(value: IProduct): void;
}

const ProductsCompanyContext = createContext<ProductsCompanyContextData>(
  {} as ProductsCompanyContextData,
);

const { REACT_APP_FIREBASE_PROJECT, REACT_APP_USE_API_PRODUCTS } = process.env;

export const ProductsCompanyProvider: React.FC = ({ children }) => {
  const { user } = useAuth();
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [products, setProducts] = useState<IProduct[]>([]);
  const [complements, setComplements] = useState<IComplement[] | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [categorySelected, setCategorySelected] = useState({} as ICategory);
  const [productSelected, setProductSelected] = useState({} as IProduct);

  const selectProduct = useCallback(
    (product: IProduct) => {
      if (product && product.uid && product.groups_complements) {
        const filter = complements?.filter(item =>
          product.groups_complements.includes(item.uid),
        );

        setProductSelected({
          ...product,
          list_complements: filter,
        });
      } else {
        setProductSelected(product);
      }
    },
    [complements],
  );

  const clearContext = useCallback(() => {
    setProducts([]);
  }, []);

  const updateProducts = useCallback(
    (product: IProduct, isNew = false) => {
      const items = [...products];

      if (isNew) {
        items.push(product);

        items.sort((a, b) =>
          a.order > b.order ? 1 : b.order > a.order ? -1 : 0,
        );

        setProducts(items);
      } else {
        const findIndex = items.findIndex(item => item.uid === product.uid);

        if (findIndex !== -1) {
          items[findIndex] = product;
          items.sort((a, b) =>
            a.order > b.order ? 1 : b.order > a.order ? -1 : 0,
          );
          setProducts(items);
        }
      }
    },
    [products],
  );

  const removeProduct = useCallback(
    (id: string) => {
      const items = [...products];

      const findIndex = items.findIndex(item => item.uid === id);

      if (findIndex !== -1) {
        items.splice(findIndex, 1);
        setProducts(items);
      }
    },
    [products],
  );

  const loadProducts = useCallback(async () => {
    await bff.delete(`/products/${REACT_APP_FIREBASE_PROJECT}`);

    // if (!isInitialized && products.length === 0) {
    const response = REACT_APP_USE_API_PRODUCTS
      ? await heroku.get('')
      : await bff.get(`/products/${REACT_APP_FIREBASE_PROJECT}`);

    const items: IProduct[] = [];
    const tmpCategories: ICategory[] = [];
    const compare = [] as string[];

    const responseProducts = response.data;

    responseProducts.forEach((item: IProduct) => {
      const data = item;

      if (!compare.includes(data.category.uid)) {
        compare.push(data.category.uid);
        tmpCategories.push(data.category);
      }

      items.push(item);
    });

    items.sort((a, b) =>
      a.order > b.order ? 1 : b.order > a.order ? -1 : 0,
    );

    setCategorySelected(tmpCategories[0]);
    setCategories(tmpCategories);
    setProducts(items);
    setIsInitialized(true);
    // }
  }, []);

  const loadComplements = useCallback(async () => {
    const response = await bff.get(
      `/complements/${REACT_APP_FIREBASE_PROJECT}`,
    );

    let comp = response.data as IComplement[];

    comp = comp.sort((a, b) => parseInt(a.order) - parseInt(b.order));

    setComplements(comp);
  }, []);

  const updateComplements = useCallback(
    (complement: IComplement, isNew = false) => {
      if (complements) {
        let items = [...complements];

        if (isNew) {
          items.push(complement);

          items = items.sort((a, b) =>
            a.order > b.order ? 1 : b.order > a.order ? -1 : 0,
          );

          setComplements(items);
        } else {
          const findIndex = items.findIndex(
            item => item.uid === complement.uid,
          );

          if (findIndex !== -1) {
            items[findIndex] = complement;

            items = items.sort((a, b) =>
              a.order > b.order ? 1 : b.order > a.order ? -1 : 0,
            );

            setComplements(items);
          }
        }
      } else {
        loadComplements();
      }
    },
    [complements, loadComplements],
  );

  const removeComplement = useCallback(
    (id: string) => {
      const items = complements ? [...complements] : [];

      const findIndex = items.findIndex(item => item.uid === id);

      if (findIndex !== -1) {
        items.splice(findIndex, 1);
        setComplements(items);
      }
    },
    [complements],
  );

  useEffect(() => {
    if (user && user.uid) loadComplements();
  }, [loadComplements, user]);

  return (
    <ProductsCompanyContext.Provider
      value={{
        categories,
        products,
        complements,
        loadProducts,
        loadComplements,
        clearContext,
        isInitialized,
        updateProducts,
        removeProduct,
        updateComplements,
        removeComplement,
        categorySelected,
        setCategorySelected,
        productSelected,
        selectProduct,
      }}
    >
      {children}
    </ProductsCompanyContext.Provider>
  );
};

export function useProductsCompany(): ProductsCompanyContextData {
  const context = useContext(ProductsCompanyContext);
  return context;
}
